import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Trans, withI18n } from 'lingui-react';
import { compose, setDisplayName } from 'recompose';

import { px2rem } from 'decisiv-ui-utils';
import { Column, Container, Row } from 'styled-components-reactive-grid';

import {
  Avatar,
  Dropdown,
  InputField,
  MessageMedium,
  MessageSmall,
  Text,
} from 'base-components';

import useContext from 'utils/useContext';
import HighlightText from 'components/HighlightText';
import TextGhostIndicator from 'elements/TextGhostIndicator';

import ConfigValueSuggestions from './ConfigValueSuggestions';
import ReportConfigBuilderContext from '../Context';
import {
  configTypes,
  configTypesPlaceholders,
  configTypesTitles,
  configTypesWarnings,
  dropdownContentStyles,
} from '../constants';

class InputFieldWithSuggestions extends Component {
  static propTypes = {
    config: PropTypes.shape({}).isRequired,
    configType: PropTypes.oneOf(Object.values(configTypes)).isRequired,
    grouped: PropTypes.bool,
    i18n: PropTypes.shape({
      _: PropTypes.func.isRequired,
    }).isRequired,
    invalidConfigKeys: PropTypes.arrayOf(
      PropTypes.oneOf(Object.values(configTypes)),
    ).isRequired,
    updateReportConfig: PropTypes.func.isRequired,
  };

  static defaultProps = {
    grouped: false,
  };

  state = {
    isOpen: false,
    wasValidWhenOpened: false,
  };

  get searchValueConfigKey() {
    return `${this.props.configType}_SEARCH_VALUE`;
  }

  get selectedItem() {
    return get(this.props.config, this.props.configType);
  }

  get searchValue() {
    return get(this.props.config, this.searchValueConfigKey, '');
  }

  get isValid() {
    const { configType, invalidConfigKeys } = this.props;
    const { isOpen, wasValidWhenOpened } = this.state;

    if (!this.searchValue) {
      return true;
    }

    return isOpen
      ? wasValidWhenOpened
      : !invalidConfigKeys.includes(configType);
  }

  handleSelect = (_, value) => {
    if (!value) {
      return;
    }

    this.props.updateReportConfig({
      [this.props.configType]: value,
      [this.searchValueConfigKey]: value,
    });
  };

  handleSearchChange = ({ target: { value } }) => {
    this.props.updateReportConfig({
      [this.searchValueConfigKey]: value,
      // If the user "backspaces" all text, treat it as clearing the selection.
      // If not, and we have a selected item, it will be treated as an error.
      ...(!value ? { [this.props.configType]: undefined } : {}),
    });
  };

  handleClearSearch = () =>
    this.props.updateReportConfig({
      [this.props.configType]: undefined,
      [this.searchValueConfigKey]: '',
    });

  handleDropdownToggle = isOpen => {
    const { invalidConfigKeys, configType } = this.props;

    const isValid = !invalidConfigKeys.includes(configType);
    const newState = { isOpen };

    if (!this.state.isOpen && isOpen) {
      newState.wasValidWhenOpened = isValid;
    }

    this.setState(newState);
  };

  renderSuggestion = (suggestion, index) => (
    <Dropdown.ListItem key={`${suggestion}_${index}`} id={suggestion}>
      <Row modifiers={['middle', 'padScale_0']} style={{ flexWrap: 'nowrap' }}>
        {this.props.grouped && (
          <Column style={{ paddingLeft: 0 }}>
            <Avatar name={suggestion} modifiers={['small']} />
          </Column>
        )}
        <Column modifiers="col">
          <Text>
            <HighlightText text={this.searchValue}>{suggestion}</HighlightText>
          </Text>
        </Column>
      </Row>
    </Dropdown.ListItem>
  );

  renderDropdown = ({ suggestions, isLoading }) => {
    const { grouped } = this.props;

    if (!this.searchValue) {
      return null;
    }

    if (!isLoading && !suggestions.length) {
      return (
        <Dropdown.Content style={dropdownContentStyles}>
          <Container
            style={{ whiteSpace: 'normal', minWidth: px2rem(300) }}
            className="no-results"
          >
            <MessageMedium>
              <MessageMedium.Header>
                <MessageMedium.Icon name="search" />
              </MessageMedium.Header>
              <MessageMedium.Section>
                <MessageMedium.Title>
                  <Trans>No Matches Found</Trans>
                </MessageMedium.Title>
              </MessageMedium.Section>
              <MessageMedium.Section>
                <MessageMedium.Text>
                  <Trans>
                    Try removing or editing some of your criteria or search for
                    different words
                  </Trans>
                </MessageMedium.Text>
              </MessageMedium.Section>
            </MessageMedium>
          </Container>
        </Dropdown.Content>
      );
    }

    if (isLoading) {
      return (
        <Dropdown.Content style={dropdownContentStyles}>
          <Dropdown.SectionHeader>
            <TextGhostIndicator
              style={{ flex: `0 0 ${px2rem(150)}`, margin: `${px2rem(5)} 0` }}
            >
              Loading...
            </TextGhostIndicator>
          </Dropdown.SectionHeader>
        </Dropdown.Content>
      );
    }

    return (
      <Dropdown.Content style={dropdownContentStyles}>
        {!grouped && (
          <Dropdown.List>
            {suggestions.map(this.renderSuggestion)}
          </Dropdown.List>
        )}

        {grouped &&
          suggestions.map(([group, items]) => (
            <Fragment key={group}>
              <Dropdown.SectionHeader>
                <Text
                  modifiers={['small', 'noWrap', 'capitalize', 'textLight']}
                >
                  {group}
                </Text>
              </Dropdown.SectionHeader>
              <Dropdown.List>{items.map(this.renderSuggestion)}</Dropdown.List>
            </Fragment>
          ))}
      </Dropdown.Content>
    );
  };

  render() {
    const { configType, grouped, i18n } = this.props;

    return (
      <Container modifiers="padScale_0" style={{ marginTop: px2rem(10) }}>
        <Dropdown
          activeItem={this.selectedItem}
          fullWidth
          hideOnChange
          onChange={this.handleSelect}
          onExpandedChange={this.handleDropdownToggle}
          zIndex={2}
        >
          {({ show }) => (
            <Fragment>
              <Container modifiers="padScale_0" onClick={show}>
                <InputField
                  isValid={this.isValid}
                  onChange={this.handleSearchChange}
                  placeholder={i18n._(configTypesPlaceholders[configType])}
                  value={this.searchValue}
                >
                  <Column modifiers={['col', 'padScaleY_0']}>
                    <Row>
                      <InputField.Label>
                        <Trans id={configTypesTitles[configType]} />
                      </InputField.Label>
                    </Row>
                    <Row>
                      <InputField.TextField />
                      {this.searchValue && (
                        <InputField.ActionButton
                          icon="times"
                          type="button"
                          onClick={this.handleClearSearch}
                          modifiers={['hoverDanger', 'padScaleX_0']}
                        />
                      )}
                    </Row>
                  </Column>
                </InputField>
              </Container>
              <ConfigValueSuggestions
                grouped={grouped}
                configType={configType}
                searchValue={this.searchValue}
              >
                {this.renderDropdown}
              </ConfigValueSuggestions>
            </Fragment>
          )}
        </Dropdown>
        {!this.isValid && (
          <Row style={{ marginTop: px2rem(5), marginBottom: px2rem(10) }}>
            <Column modifiers={['col', 'padScale_0']}>
              <MessageSmall type="warning">
                {i18n._(configTypesWarnings[configType])}
              </MessageSmall>
            </Column>
          </Row>
        )}
      </Container>
    );
  }
}

export default compose(
  setDisplayName('InputFieldWithSuggestions'),
  withI18n(),
  useContext(ReportConfigBuilderContext),
)(InputFieldWithSuggestions);



// WEBPACK FOOTER //
// ./src/compositions/Reports/ReportConfigBuilder/InputFieldWithSuggestions/index.js